import { useMemo, useState } from 'react';
import {
  getRegionId,
  regionHasNeighbor,
  RFM_GRID_COLS,
  RFM_GRID_ROWS,
  RFM_REGION_ANCHORS,
  RFM_REGION_TO_GROUP_ID,
  type RfmGroupId,
} from '@/components/tree-dnd/tree-dnd-conditions/rfm-group-grid-config';
import { useI18nContext } from '@/i18n/i18n-react';
import { cn } from '@/lib/utils';

interface RfmGroupGridProps {
  selectedIds: string[];
  onChange: (ids: string[]) => void;
}

function useRfmGroupLabels(): Record<RfmGroupId, string> {
  const { LL } = useI18nContext();

  return useMemo(
    () => ({
      champions: LL.audiences.filters.rfmGroups.champions(),
      loyal: LL.audiences.filters.rfmGroups.loyal(),
      potential: LL.audiences.filters.rfmGroups.potential(),
      new: LL.audiences.filters.rfmGroups.new(),
      promising: LL.audiences.filters.rfmGroups.promising(),
      needAttention: LL.audiences.filters.rfmGroups.needAttention(),
      aboutToSleep: LL.audiences.filters.rfmGroups.aboutToSleep(),
      'at-risk': LL.audiences.filters.rfmGroups.atRisk(),
      'cannot-lose': LL.audiences.filters.rfmGroups.cannotLose(),
      hibernating: LL.audiences.filters.rfmGroups.hibernating(),
      lost: LL.audiences.filters.rfmGroups.lost(),
    }),
    [LL]
  );
}

function RfmGridCell({
  row,
  col,
  regionId,
  groupId,
  label,
  showLabel,
  selected,
  groupHovered,
  onToggle,
  onRegionHover,
}: {
  row: number;
  col: number;
  regionId: number;
  groupId: RfmGroupId;
  label: string;
  showLabel: boolean;
  selected: boolean;
  groupHovered: boolean;
  onToggle: (groupId: RfmGroupId) => void;
  onRegionHover: (regionId: number) => void;
}) {
  const hasTopNeighbor = regionHasNeighbor(row, col, -1, 0, regionId);
  const hasLeftNeighbor = regionHasNeighbor(row, col, 0, -1, regionId);
  const hasRightNeighbor = regionHasNeighbor(row, col, 0, 1, regionId);
  const hasBottomNeighbor = regionHasNeighbor(row, col, 1, 0, regionId);

  const exteriorTop = row === 0 || !hasTopNeighbor;
  const exteriorLeft = col === 0 || !hasLeftNeighbor;
  const exteriorBottom = row === RFM_GRID_ROWS - 1 || !hasBottomNeighbor;
  const exteriorRight = col === RFM_GRID_COLS - 1 || !hasRightNeighbor;

  const insetSelectionShadow = getInsetSelectionShadow({
    selected,
    exteriorTop,
    exteriorLeft,
    exteriorBottom,
    exteriorRight,
  });

  return (
    <button
      type="button"
      style={
        insetSelectionShadow ? { boxShadow: insetSelectionShadow } : undefined
      }
      className={cn(
        'relative flex h-full w-full min-h-0 min-w-0 items-center justify-center border-solid p-1 text-center text-xs font-medium leading-tight transition-[colors,box-shadow]',
        'bg-sky-100 dark:bg-sky-950/40',
        groupHovered && 'bg-sky-200/80 dark:bg-sky-900/50',
        row === 0 ? 'border-t-4 border-t-background' : 'border-t-0',
        col === 0 ? 'border-l-4 border-l-background' : 'border-l-0',
        exteriorBottom ? 'border-b-4 border-b-background' : 'border-b-0',
        exteriorRight ? 'border-r-4 border-r-background' : 'border-r-0'
      )}
      aria-pressed={selected}
      aria-label={label}
      onMouseEnter={() => onRegionHover(regionId)}
      onClick={() => onToggle(groupId)}
    >
      {showLabel ? (
        <span className="line-clamp-3 text-pretty">{label}</span>
      ) : null}
    </button>
  );
}

function getInsetSelectionShadow({
  selected,
  exteriorTop,
  exteriorLeft,
  exteriorBottom,
  exteriorRight,
}: {
  selected: boolean;
  exteriorTop: boolean;
  exteriorLeft: boolean;
  exteriorBottom: boolean;
  exteriorRight: boolean;
}): string | undefined {
  if (!selected) return undefined;

  const shadows: string[] = [];

  if (exteriorTop) shadows.push('inset 0 2px 0 0 hsl(var(--primary))');
  if (exteriorBottom) shadows.push('inset 0 -2px 0 0 hsl(var(--primary))');
  if (exteriorLeft) shadows.push('inset 2px 0 0 0 hsl(var(--primary))');
  if (exteriorRight) shadows.push('inset -2px 0 0 0 hsl(var(--primary))');

  return shadows.length > 0 ? shadows.join(', ') : undefined;
}

export function RfmGroupGrid({ selectedIds, onChange }: RfmGroupGridProps) {
  const labels = useRfmGroupLabels();
  const [hoveredRegionId, setHoveredRegionId] = useState<number | null>(null);

  const toggleGroup = (groupId: RfmGroupId) => {
    const next = selectedIds.includes(groupId)
      ? selectedIds.filter((id) => id !== groupId)
      : [...selectedIds, groupId];
    onChange(next);
  };

  return (
    <div
      className="grid aspect-square w-full grid-cols-5 grid-rows-5 gap-0 bg-background"
      role="group"
      aria-label="RFM segments"
      onMouseLeave={() => setHoveredRegionId(null)}
    >
      {Array.from({ length: RFM_GRID_ROWS }, (_, row) =>
        Array.from({ length: RFM_GRID_COLS }, (_, col) => {
          const regionId = getRegionId(row, col);
          const groupId = RFM_REGION_TO_GROUP_ID[regionId];
          const anchor = RFM_REGION_ANCHORS[regionId];
          const showLabel = anchor.row === row && anchor.col === col;
          const selected = selectedIds.includes(groupId);
          const groupHovered = hoveredRegionId === regionId;

          return (
            <RfmGridCell
              key={`${row}-${col}`}
              row={row}
              col={col}
              regionId={regionId}
              groupId={groupId}
              label={labels[groupId]}
              showLabel={showLabel}
              selected={selected}
              groupHovered={groupHovered}
              onToggle={toggleGroup}
              onRegionHover={setHoveredRegionId}
            />
          );
        })
      )}
    </div>
  );
}

export { useRfmGroupLabels };
